import { useState, useEffect } from "react";
import { Check, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { PBX_API_ORIGIN } from "@/lib/apiConfig";

const CURRENCY_SYMBOL = { INR: "₹", USD: "$" };

function formatPerExtension(plan) {
  const symbol = CURRENCY_SYMBOL[plan.currency] || "";
  const amount = plan.currency === "USD" ? plan.price_per_extension / 100 : plan.price_per_extension / 100;
  const display = plan.currency === "USD" ? amount.toFixed(2).replace(/\.00$/, "") : amount.toLocaleString("en-IN");
  return `${symbol}${display}`;
}

function formatMonthly(plan) {
  const symbol = CURRENCY_SYMBOL[plan.currency] || "";
  const amount = (plan.price_monthly || 0) / 100;
  const display = plan.currency === "USD" ? amount.toFixed(2).replace(/\.00$/, "") : amount.toLocaleString("en-IN");
  return `${symbol}${display}`;
}

// Pricing is a monthly price, full stop. The page used to offer Monthly /
// Quarterly / Half-yearly / Yearly with discounts up to 15%, which nothing
// in the system ever applied - no cycle reached the backend, and no discount
// was ever charged. A price shown on a marketing page has to be the price.

export const Pricing = () => {
  const [product, setProduct] = useState("pbx");
  const [region, setRegion] = useState("IN");
  const [telephony, setTelephony] = useState(0);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${PBX_API_ORIGIN}/api/signup/plans?product=${product}&region=${region}&telephony=${telephony}`)
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) return setPlans([]);
        const filtered =
          product === "ai"
            ? data.filter((p) => p.product === "ai" || p.product === "combo")
            : data.filter((p) => p.product === "pbx" && p.region === region);
        filtered.sort((a, b) => (a.price_monthly || 0) - (b.price_monthly || 0));
        setPlans(filtered);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [product, region, telephony]);

  const salesOnly = plans.length === 1 && plans[0].contact_sales && !plans.some((p) => !p.contact_sales);
  const aiSingle = product === "ai" && plans.length === 1 && !plans[0].contact_sales;
  const aiExtraFeatures = [
    "Configurable escalation rules",
    "SMS follow-up after calls",
    "Unanswered-questions log",
    "Visual, drag-and-drop IVR flow builder",
    "Expanded multilingual auto-detect (beyond EN · HI · Hinglish)",
  ];

  return (
    <section id="pricing" className="relative py-24 md:py-32 bg-[#0a0a0a] border-t border-white/10" data-testid="pricing-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <Reveal>
          <p className="font-mono-t text-xs uppercase tracking-[0.25em] text-[#5B8CFF] mb-4">Pricing</p>
          <h2 className="font-heading font-medium tracking-tight text-3xl md:text-4xl lg:text-5xl max-w-2xl text-white">
            {product === "ai" ? "Simple pricing for the AI agent." : "Simple, per-extension pricing."}
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <div className="inline-flex border border-white/15" data-testid="pricing-product-toggle">
              {[{ key: "pbx", label: "PBX Platform" }, { key: "ai", label: "AI Voice Agent" }].map((pr) => (
                <button
                  key={pr.key}
                  onClick={() => setProduct(pr.key)}
                  className={`px-5 py-2.5 text-sm font-semibold transition-colors ${product === pr.key ? "bg-[#5B8CFF] text-white" : "bg-transparent text-neutral-400 hover:bg-white/5"}`}
                >
                  {pr.label}
                </button>
              ))}
            </div>
            {product === "pbx" && (
              <>
                <div className="inline-flex border border-white/15" data-testid="pricing-region-toggle">
                  {[{ key: "IN", label: "🇮🇳 India" }, { key: "US", label: "🇺🇸 USA" }].map((r) => (
                    <button
                      key={r.key}
                      onClick={() => setRegion(r.key)}
                      className={`px-5 py-2.5 text-sm font-semibold transition-colors ${region === r.key ? "bg-white text-neutral-900" : "bg-transparent text-neutral-400 hover:bg-white/5"}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <div className="inline-flex border border-white/15" data-testid="pricing-telephony-toggle">
                  {[{ key: 0, label: "Platform only" }, { key: 1, label: "Platform + Telephony" }].map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setTelephony(t.key)}
                      className={`px-5 py-2.5 text-sm font-semibold transition-colors ${telephony === t.key ? "bg-white text-neutral-900" : "bg-transparent text-neutral-400 hover:bg-white/5"}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <p className="mt-3 text-xs text-neutral-500 max-w-lg">
            {product === "ai"
              ? "One flat monthly plan, same worldwide — usage-priced per AI-agent minute on top, no per-extension math."
              : telephony === 0
              ? "Bring your own carrier/trunk for calling — you control call costs directly."
              : "Calling is included — truly unlimited, no per-minute charges from us."}
          </p>
        </Reveal>

        {loading ? (
          <div className="mt-14 flex items-center justify-center py-20 text-neutral-400">
            <Loader2 size={22} className="animate-spin mr-3" /> Loading pricing…
          </div>
        ) : salesOnly ? (
          <Reveal delay={0.15}>
            <div className="mt-14 border border-white/10 bg-white/[0.03] text-white p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <h3 className="font-heading font-semibold text-2xl">{plans[0].name}</h3>
                <ul className="mt-4 space-y-2">
                  {plans[0].features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-neutral-300">
                      <Check size={16} className="mt-0.5 shrink-0 text-white" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
              <Link to="/demo" className="shrink-0 inline-flex items-center gap-2 rounded-full bg-white text-black font-bold px-8 py-4 hover:bg-neutral-200 transition-colors">
                Contact sales
              </Link>
            </div>
          </Reveal>
        ) : aiSingle ? (
          <Reveal delay={0.15}>
            <div className="mt-14 border border-white/10 bg-white/[0.03] text-white" data-testid={`pricing-${plans[0].key}`}>
              <div className="grid md:grid-cols-[minmax(0,340px)_1fr]">
                <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-white/10 flex flex-col">
                  <h3 className="font-heading font-semibold text-2xl">{plans[0].name}</h3>
                  <div className="mt-5">
                    <p className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">Base plan</p>
                    <div className="flex items-end gap-1">
                      <span className="font-heading font-medium text-4xl tracking-tight">{formatMonthly(plans[0])}</span>
                      <span className="text-sm mb-1 text-neutral-400">/mo</span>
                    </div>
                  </div>
                  {plans[0].usage_note && (
                    <div className="mt-5 pt-5 border-t border-white/10">
                      <p className="font-mono-t text-[10px] uppercase tracking-widest text-[#5B8CFF]">AI usage</p>
                      <div className="flex items-end gap-1.5">
                        <span className="font-heading font-semibold text-5xl tracking-tight text-[#5B8CFF]">₹4.50</span>
                        <span className="text-base mb-1.5 text-[#5B8CFF]/80">/ AI-agent minute</span>
                      </div>
                      <p className="mt-1 text-xs text-neutral-400">Pay only for the minutes your AI agent actually talks.</p>
                    </div>
                  )}
                  <Link
                    to="/signup"
                    className="mt-8 text-center font-bold py-3.5 text-sm rounded-full bg-white text-black hover:bg-neutral-200 transition-colors duration-300"
                    data-testid={`pricing-cta-${plans[0].key}`}
                  >
                    Get started
                  </Link>
                </div>
                <div className="p-8 md:p-10">
                  <p className="font-mono-t text-[11px] uppercase tracking-widest text-neutral-500 mb-4">What's included</p>
                  <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {[...plans[0].features, ...aiExtraFeatures].map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-neutral-300">
                        <Check size={16} className="mt-0.5 shrink-0 text-white" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>
        ) : (
          <div className={`mt-14 grid gap-px bg-white/10 border border-white/10 ${plans.length === 3 ? "md:grid-cols-3" : plans.length === 2 ? "md:grid-cols-2" : ""}`}>
            {plans.map((p, i) => {
              const highlighted = product === "ai" ? p.key === "ai_business" : p.name === "Business";
              return (
                <Reveal key={p.key} delay={i * 0.08}>
                  <div
                    className={`relative h-full p-7 flex flex-col transition-colors duration-300 text-white overflow-hidden ${highlighted ? "bg-white/[0.05]" : "bg-white/[0.02] hover:bg-white/[0.04]"}`}
                    data-testid={`pricing-${p.key}`}
                  >
                    {highlighted && (
                      <>
                        <span className="absolute top-0 left-0 right-0 h-[2px] bg-[#5B8CFF] z-10" />
                        <div
                          className="pointer-events-none absolute inset-0"
                          style={{ background: "radial-gradient(ellipse 90% 55% at 50% 0%, rgba(255,150,40,0.3), rgba(255,150,40,0) 70%)" }}
                        />
                      </>
                    )}
                    <div className="relative flex-1 flex flex-col">
                      <h3 className="font-heading font-semibold text-xl">{p.name}</h3>
                      <p className="mt-2 text-xs min-h-[32px] text-neutral-400">
                        {p.price_per_extension
                          ? `${p.contact_sales ? `${p.min_extensions}+` : `${p.min_extensions}–${p.max_extensions}`} extensions`
                          : p.max_extensions
                          ? `Up to ${p.max_extensions} extensions`
                          : ""}
                        {p.channels_per_extension
                          ? ` · channels = ${p.channels_per_extension}x extensions`
                          : p.max_concurrent_calls
                          ? ` · ${p.max_concurrent_calls} concurrent calls`
                          : ""}
                      </p>
                      <div className="mt-6 flex items-end gap-1">
                        {p.contact_sales ? (
                          <span className="font-heading font-medium text-4xl tracking-tight">Custom</span>
                        ) : p.price_per_extension ? (
                          <>
                            <span className="font-heading font-medium text-4xl tracking-tight">{formatPerExtension(p)}</span>
                            <span className="text-sm mb-1 text-neutral-400">/extension/mo</span>
                          </>
                        ) : (
                          <>
                            <span className="font-heading font-medium text-4xl tracking-tight">{formatMonthly(p)}</span>
                            <span className="text-sm mb-1 text-neutral-400">/mo</span>
                          </>
                        )}
                      </div>
                      <ul className="mt-6 space-y-3 flex-1">
                        {p.features.map((f) => (
                          <li key={f} className="flex items-start gap-2.5 text-sm text-neutral-300">
                            <Check size={16} className="mt-0.5 shrink-0 text-white" />
                            {f}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to={p.contact_sales ? "/demo" : "/signup"}
                        className={`mt-8 text-center font-bold py-3 text-sm rounded-full transition-colors duration-300 ${
                          highlighted ? "bg-white text-black hover:bg-neutral-200" : "border border-white/20 text-white hover:border-white/50 hover:bg-white/5"
                        }`}
                        data-testid={`pricing-cta-${p.key}`}
                      >
                        {p.contact_sales ? "Contact sales" : "Get started"}
                      </Link>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}

        <Reveal delay={0.2}>
          <div className="mt-8 flex items-center justify-between flex-wrap gap-4 border border-white/10 bg-white/[0.03] px-6 py-5">
            {product === "ai" ? (
              <>
                <p className="text-sm text-neutral-300">Also need a full phone system, not just the AI agent?</p>
                <button onClick={() => setProduct("pbx")} className="text-sm font-semibold text-[#5B8CFF] hover:underline">See PBX Platform pricing →</button>
              </>
            ) : (
              <>
                <p className="text-sm text-neutral-300">Want the AI Voice Agent added on top of your PBX?</p>
                <button onClick={() => setProduct("ai")} className="text-sm font-semibold text-[#5B8CFF] hover:underline">See AI Voice Agent pricing →</button>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
